import { addHours, endOfDay, format as originalFormat, startOfDay } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { memo, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useQueryGetGetFlights } from '../../../backend';
import { useCurrentUserClient } from '../../../contexts/OidcContext/OidcContext.selectors';
import { StyledBox } from '../../Resources/ResourceContent/ResourceFields/ResourceFieldsLine/ResourceFieldsLineContainer.style';
import { Gantt } from './Gantt/Gantt';
import { ganttDayFormat, ganttPxPerMs } from './Gantt/gantt.constants';
import { GanttTurnaroundsRowBox } from './Gantt/Gantt.style';
import { getProposedAllocationByTurnaround, getResourceName, getTurnaroundId } from './Gantt/gantt.utils';
import { GanttAllocationControl } from './Gantt/GanttAllocationControl';
import { GanttOverCapacityAlerts } from './Gantt/GanttOverCapacityAlert/GanttOverCapacityAlert';
import { GanttTurnaroundRow } from './Gantt/GanttRow/GanttTurnaroundRow';
import { GanttResourceGroups } from './Gantt/Groups/GanttResourceGroups';
import { GanttSelection } from './Gantt/TurnaroundSelection/GanttSelection';
import { StyledOldTurnaroundBox } from './RmsContent.style';
import { getTurnaroundActualOccupation } from './RmsContent.utils';
import { useRmsGanttValue, useSetRmsGantt } from './RmsContext';

export const RmsContent = memo(function RmsContent({
    shallGanttAutoScroll,
    disableGanttAutoScroll,
}: {
    shallGanttAutoScroll: boolean;
    disableGanttAutoScroll: () => void;
}) {
    const client = useCurrentUserClient();
    const day = useRmsGanttValue('day');
    const dayBounds = useRmsGanttValue('dayBounds');
    const resourcesByType = useRmsGanttValue('resourcesByType');
    const resourceType = useRmsGanttValue('resourceType');
    const isFocused = useRmsGanttValue('isFocused');
    const nbUpdateAllocations = useRmsGanttValue('nbUpdateAllocations');
    const selectedTurnarounds = useRmsGanttValue('selectedTurnarounds');
    const setDayBounds = useSetRmsGantt('dayBounds');
    const proposedAllocation = useRmsGanttValue('proposedAllocation');
    const { data: ganttDto } = useQueryGetGetFlights({
        selectedDay: originalFormat(day, ganttDayFormat),
        selectedResourceType: resourceType,
    });
    const turnarounds = ganttDto?.turnarounds;

    const selectedTurnaroundOccupation = getTurnaroundActualOccupation(selectedTurnarounds?.[0]);

    const isProposition = useMemo(() => {
        return (
            selectedTurnarounds &&
            proposedAllocation &&
            getProposedAllocationByTurnaround(proposedAllocation, selectedTurnarounds[0]) &&
            getProposedAllocationByTurnaround(proposedAllocation, selectedTurnarounds[0]).resource !==
                getResourceName(selectedTurnarounds[0])
        );
    }, [proposedAllocation, selectedTurnarounds]);

    useEffect(() => {
        const startDay = zonedTimeToUtc(
            addHours(startOfDay(day), client.startDayHourShift || 0),
            client.timeZoneId || '',
        ).getTime();
        const endDay = zonedTimeToUtc(
            addHours(endOfDay(day), client.startDayHourShift || 0),
            client.timeZoneId || '',
        ).getTime();
        setDayBounds({ from: startDay, to: endDay });
    }, [client.airportIcao, client.startDayHourShift, client.timeZoneId, day, resourceType, setDayBounds, turnarounds]);

    return (
        <>
            <Gantt shallGanttAutoScroll={shallGanttAutoScroll} disableGanttAutoScroll={disableGanttAutoScroll}>
                {selectedTurnarounds?.map(t => (
                    <GanttSelection
                        selectedTurnaround={t}
                        key={
                            getTurnaroundId(t) +
                            t.considerArrivalFlight +
                            t.considerDepartureFlight +
                            t.actualOccupation.occupationStart.time
                        }
                    />
                ))}

                {turnarounds?.length > 0 && resourcesByType && (
                    <GanttResourceGroups>
                        {(turnaroundsRow, resourceName) => {
                            const oldResource = getResourceName(selectedTurnarounds[0]) === resourceName;
                            return (
                                <>
                                    {nbUpdateAllocations === 0 && resourceName !== '' && (
                                        <GanttOverCapacityAlerts resourceName={resourceName} />
                                    )}
                                    <GanttTurnaroundsRowBox key={resourceName}>
                                        {turnaroundsRow.length === 0 && (
                                            <GanttTurnaroundRow
                                                key={resourceName}
                                                turnaroundsOnRow={[]}
                                                resource={resourcesByType?.find(r => r.name === resourceName)}
                                            />
                                        )}
                                        {turnaroundsRow?.map((turnarounds, index) => (
                                            <GanttTurnaroundRow
                                                key={resourceName + index}
                                                index={index}
                                                turnaroundsOnRow={turnarounds}
                                                resource={resourcesByType?.find(r => r.name === resourceName)}
                                            />
                                        ))}
                                        {nbUpdateAllocations > 0 &&
                                            isProposition &&
                                            isFocused &&
                                            oldResource &&
                                            selectedTurnaroundOccupation && (
                                                <StyledOldTurnaroundBox
                                                    width={`${
                                                        (selectedTurnaroundOccupation.occupationEnd?.time -
                                                            selectedTurnaroundOccupation.occupationStart?.time) *
                                                        ganttPxPerMs *
                                                        2
                                                    }px`}
                                                    ml={`${
                                                        (selectedTurnaroundOccupation.occupationStart?.time -
                                                            dayBounds.from) *
                                                        ganttPxPerMs *
                                                        2
                                                    }px`}
                                                />
                                            )}
                                    </GanttTurnaroundsRowBox>
                                </>
                            );
                        }}
                    </GanttResourceGroups>
                )}
                {resourcesByType && resourcesByType.length === 0 && (
                    <StyledBox p={5} color={'white'} fontSize={'24px'} fontWeight={'400'}>
                        <FormattedMessage id={'rms.noResources'} values={{ resourceType: resourceType }} />
                    </StyledBox>
                )}
            </Gantt>
            <GanttAllocationControl />
        </>
    );
});
